import { Button, Flex, Table, Tag, Form, Select, Input, Col, Row } from "antd";
import React, { useState } from "react";
import Title from "antd/es/typography/Title";
import { useEffect } from "react";
import { colors } from "../../../../../constants/colors";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Spinner from "../../../../common/spinner/Spinner";
import { appConstConfig } from "../../../../../constants/config/appConstConfig";
import {
  getServiceProviderServicesAction,
  updateOrganizationServicesStatusAction,
} from "../../../../../redux/services/service-providers/serviceProvidersServices";
import { dateObjToDispViewDate, getStatusNameInArabicAsWell } from "../../../../../utils/utils";
import { ArrowLeftOutlined, SearchOutlined } from "@ant-design/icons";
import styles from "./ServiceProvider.module.scss";
import useDebounce from "../../../../../hooks/useDebounce";

const ServiceProviderDetailsServices = () => {
  /* i18n dependencies */
  const { t } = useTranslation();
  /* i18n dependencies */

  /* For navigating between pages */
  const navigate = useNavigate();
  /* For navigating between pages */

  /* form dependencies */
  const [form] = Form.useForm();
  /* form dependencies */

  /* dispatch an action */
  const dispatch = useDispatch();
  /* dispatch an action */

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  const [selectListLoader, setSelectListLoader] = useState(false);
  /* loading dependencies */

  /* id dependencies */
  const { serviceProviderServices } = useSelector(
    (state) => state.serviceProviders
  );

  const {
    selectedServiceProvider: { selectedServiceProviderId, navigateBack },
  } = useSelector((state) => state.serviceProviders);
  /* id dependencies */


  /* table dependencies */
  const [columns, setColumns] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [pagination, setPagination] = useState({
    pageNo: appConstConfig.DEFAULT_TABLE_PAGE_NUMBER,
    pageSize: appConstConfig.DEFAULT_TABLE_PAGE_SIZE,
  });
  const paginationObj = {
    total: serviceProviderServices?.total,
    current: pagination?.pageNo,
    pageSize: pagination?.pageSize,
    onChange: (pageNo, pageSize) => setPagination({ pageNo, pageSize }),
    pageSizeOptions: appConstConfig.DEFAULT_PAGE_SIZE_OPTIONS_ARR,
    showSizeChanger: true,
  };
  const { debouncedValue: debouncedSearchValue, debounceLoading } = useDebounce(
    searchValue,
    500
  );
  /* table dependencies */

  useEffect(() => {
    handleGetTableData();
  }, [pagination, debouncedSearchValue]);

  const handleGetTableData = () => {
    /* initial dependencies - Edit */
    if (
      selectedServiceProviderId &&
      selectedServiceProviderId !== "" &&
      selectedServiceProviderId !== null &&
      selectedServiceProviderId !== undefined
    ) {
      const dataToBeSent = {
        page: pagination?.pageNo,
        limit: pagination?.pageSize,
        selectedOrganizationId: selectedServiceProviderId,
        search:debouncedSearchValue
      };
      setLoading(true);
      dispatch(getServiceProviderServicesAction(dataToBeSent, setLoading));
    }
    /* initial dependencies - Edit */
  };

  /* on status changed */
  const onStatusChanged = (id, value, record) => {
    const dataToBeSent = {};
    dataToBeSent.selectedOrganizationId = record?._id;
    dataToBeSent["is_active"] = value?.value === "active" ? true : false;

    dispatch(
      updateOrganizationServicesStatusAction(dataToBeSent, setLoading)
    ).then(() => {
      handleGetTableData();
    });
  };
  /* on status changed */

  /* initial dependencies rendering */
  useEffect(() => {
    /* table column config */
    const tempTableColumns = [
      {
        title: t("appointmentInfo.dateOfJoining"),
        dataIndex: "created_at",
        key: "created_at",
        render: (_, record) => (
          <p>{dateObjToDispViewDate(record?.created_at, "ddmmyyyy")}</p>
        ),
      },
      {
        title: t("appointmentInfo.service"),
        dataIndex: "title",
        key: "title",
      },
      {
        title: t("appointmentInfo.category"),
        dataIndex: "category",
        key: "category",
        render: (_, record) => `${record?.categoryInfo?.category?.name || "-"}`,
      },
      {
        title: t("appointmentInfo.subCategory"),
        dataIndex: "subcategory",
        key: "subcategory",
        render: (_, record) => `${record?.categoryInfo?.subcategory?.name || "-"}`,
      },
      {
        title: t("appointmentInfo.status"),
        key: "action",
        align: "center",
        render: (_, record) => {
          const getColorBasedOnStatus = (status) => {
            switch (status) {
              case "active":
                return { background: colors.lightTeal, color: colors.darkTeal };
    
              case "inactive":
                return {
                  background: colors.lightPinkishRed,
                  color: colors.darkMaroon,
                };
    
              default:
                break;
            }
          };
          const is_active = record?.is_active ? "active" : "inactive";
    
          return (
            <>
              <Tag
                bordered={false}
                style={{ ...getColorBasedOnStatus(is_active), textTransform: 'capitalize' }}
              >
                {getStatusNameInArabicAsWell(is_active)}
              </Tag>
            </>
          );
        },
      },
      {
        title: t("appointmentInfo.action"),
        key: "action",
        align: "center",
        render: (_, record) => {
          const is_active = record?.is_active ? "active" : "inactive";
          return (
            <>
              <Select
                options={[
                  { value: "active", label: t("CommonUtils.active")},
                  { value: "inactive", label: t("CommonUtils.inactive") },
                ]}
                placeholder={t("appointmentInfo.selectStatus")}
                defaultValue={is_active}
                style={{ width: 120 }}
                onChange={(id, value) => onStatusChanged(id, value, record)}
              />
            </>
          );
        },
      },
      {
        title: t("appointmentInfo.soldCount"),
        dataIndex: "total_sold_count",
        key: "total_sold_count",
        render: (_, record) => `${record?.total_sold_count}`,
      },
      {
        title: t("appointmentInfo.price"),
        dataIndex: "price",
        key: "price",
        render: (_, record) => `${record?.price || "-"}`,
      }
    ];
    
    /* table column config */
    setColumns(tempTableColumns);
  }, []);
  /* initial dependencies rendering */

  return (
    <div className="servicesListContainer">
      <Flex vertical gap="2rem">
        <Flex gap="1rem" align="center">
          <Button
            type="default"
            shape="circle"
            size="small"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(navigateBack)}
          />
          <Input
            size="large"
            suffix={<SearchOutlined />}
            placeholder={t("Modules.customer.search")}
            onChange={(event) => setSearchValue(event.target.value)}
            style={{ width: "370px" }}
          />
        </Flex>
        {loading || debounceLoading ? (
          <Spinner />
        ) : (
          <Table
            dataSource={serviceProviderServices?.data}
            columns={columns}
            pagination={paginationObj}
          />
        )}
      </Flex>
    </div>
  );
};

export default ServiceProviderDetailsServices;
